(() => {
  const selector = '.btm-duty-ack__checkbox';
  const checkoutSelector = 'button[name="checkout"], input[name="checkout"], .cart__checkout-button';
  const attribute = 'Import duties acknowledged';
  const approvedForms = new WeakSet();
  const errorMessages = new WeakMap();
  let acknowledged = false;
  let saved = false;
  let revision = 0;
  let submitting = false;
  let saves = Promise.resolve();
  let scheduled = false;

  try { localStorage.removeItem('btmDutyAck'); } catch {}

  const boxes = () => Array.from(document.querySelectorAll(selector));
  const checkboxFor = form => boxes().find(box => box.form === form);
  const empty = element => Boolean(element?.closest('cart-drawer.is-empty, #main-cart-footer.is-empty'));
  const valid = box => Boolean(acknowledged && box?.checked && box.isConnected && !empty(box));

  function showError(box, failedSave = false) {
    const error = box?.closest('.btm-duty-ack')?.querySelector('.btm-duty-ack__error');
    if (!error) return;
    if (!errorMessages.has(error)) errorMessages.set(error, error.textContent);
    error.textContent = failedSave ? window.cartStrings.error : errorMessages.get(error);
    error.hidden = false;
    box.setAttribute('aria-invalid', 'true');
    box.focus();
  }

  function hideErrors() {
    document.querySelectorAll('.btm-duty-ack__error').forEach(error => { error.hidden = true; });
    boxes().forEach(box => box.removeAttribute('aria-invalid'));
  }

  function render() {
    const inputs = boxes();
    if (acknowledged && inputs.length && inputs.every(empty)) {
      acknowledged = false;
      saved = false;
      revision += 1;
    }
    inputs.forEach(box => { box.checked = acknowledged && !empty(box); });
    document.querySelectorAll(checkoutSelector).forEach(button => {
      const box = checkboxFor(button.form);
      if (!box) return;
      const disabled = !valid(box) || submitting;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.setAttribute('aria-busy', String(submitting));
      button.classList.toggle('btm-checkout-disabled', disabled);
    });
    document.querySelectorAll('[data-duty-ack-accelerated]').forEach(container => {
      const box = checkboxFor(document.getElementById(container.dataset.dutyAckAccelerated));
      const disabled = !valid(box) || !saved || submitting;
      container.hidden = disabled;
      container.inert = disabled;
      container.style.display = disabled ? 'none' : '';
    });
  }

  function save(checked, version) {
    const pending = saves.catch(() => {}).then(async () => {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [attribute]: checked ? 'Yes' : '' } })
      });
      if (!response.ok) throw new Error('Cart acknowledgement update failed');
      const cart = await response.json();
      if ((cart.attributes?.[attribute] === 'Yes') !== checked) {
        throw new Error('Cart acknowledgement was not saved');
      }
      if (version === revision) saved = checked;
    });
    saves = pending;
    return pending;
  }

  document.addEventListener('change', event => {
    if (!(event.target instanceof Element) || !event.target.matches(selector)) return;
    acknowledged = event.target.checked;
    saved = false;
    const version = ++revision;
    hideErrors();
    render();
    save(acknowledged, version).catch(() => {
      if (version === revision && acknowledged) showError(event.target, true);
    }).finally(render);
  });

  document.addEventListener('invalid', event => {
    if (event.target.matches?.(selector)) showError(event.target);
  }, true);

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !['cart', 'CartDrawer-Form'].includes(form.id)) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    const box = checkboxFor(form);
    if (approvedForms.has(form) && valid(box)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!valid(box)) { showError(box); render(); return; }
    if (submitting) return;
    submitting = true;
    const version = revision;
    const submitter = event.submitter || document.querySelector(`${checkoutSelector.split(',').map(part => `${part.trim()}[form="${form.id}"]`).join(',')}`);
    render();
    try {
      await window.btmInstagramCart?.flush().catch(() => {});
      await save(true, version);
      if (version !== revision || !valid(box) || !form.isConnected) return;
      submitting = false;
      render();
      approvedForms.add(form);
      try { form.requestSubmit(submitter || undefined); }
      finally { approvedForms.delete(form); }
    } catch {
      if (version === revision && box.isConnected) showError(box, true);
    } finally {
      submitting = false;
      render();
    }
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || !/\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(destination.pathname)) return;
    const form = document.getElementById(link.closest('cart-drawer') ? 'CartDrawer-Form' : 'cart') || document.getElementById('CartDrawer-Form');
    const box = checkboxFor(form);
    if (!box) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!valid(box)) { showError(box); return; }
    const button = Array.from(document.querySelectorAll(checkoutSelector)).find(candidate => candidate.form === form);
    form.requestSubmit(button || undefined);
  }, true);

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; render(); });
  }

  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  function reset() {
    acknowledged = false;
    saved = false;
    submitting = false;
    revision += 1;
    hideErrors();
    render();
  }
  window.addEventListener('pagehide', reset);
  window.addEventListener('pageshow', () => {
    reset();
    requestAnimationFrame(render);
  });
  render();
})();
